import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FaExternalLinkAlt, FaCopy, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import { isEmbeddedBrowser } from '../lib/firebase';

const EmbeddedBrowserModal = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [copied, setCopied] = useState(false);
  const [detectedBrowser, setDetectedBrowser] = useState('');
  
  const currentUrl = window.location.href;
  
  // Detect which embedded browser we're in
  useEffect(() => {
    if (isOpen) {
      const ua = navigator.userAgent.toLowerCase();
      if (ua.includes('linkedin')) setDetectedBrowser('LinkedIn');
      else if (ua.includes('fban') || ua.includes('fbav') || ua.includes('facebook')) setDetectedBrowser('Facebook');
      else if (ua.includes('instagram')) setDetectedBrowser('Instagram');
      else if (ua.includes('twitter')) setDetectedBrowser('Twitter/X');
      else if (ua.includes('tiktok')) setDetectedBrowser('TikTok');
      else if (ua.includes('whatsapp')) setDetectedBrowser('WhatsApp');
      else if (ua.includes('telegram')) setDetectedBrowser('Telegram');
      else if (ua.includes('snapchat')) setDetectedBrowser('Snapchat');
      else if (ua.includes('messenger')) setDetectedBrowser('Messenger');
      else if (ua.includes('wechat') || ua.includes('micromessenger')) setDetectedBrowser('WeChat');
      else setDetectedBrowser(t('embeddedBrowser.inAppBrowser', 'in-app browser'));
    }
  }, [isOpen, t]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = currentUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenInBrowser = () => {
    // Try to open in external browser (works on some platforms)
    // For iOS: Try using a special URL scheme
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    
    if (isIOS) {
      // Try Safari
      window.location.href = `x-safari-${currentUrl}`;
      // Fallback - some apps support this
      setTimeout(() => {
        window.open(currentUrl, '_system');
      }, 100);
    } else if (isAndroid) {
      // Try intent URL for Android
      const intentUrl = `intent://${currentUrl.replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;end`;
      window.location.href = intentUrl;
    } else {
      window.open(currentUrl, '_blank');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25 }}
          className={`bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 ${isRTL ? 'text-right' : 'text-left'}`}
          onClick={(e) => e.stopPropagation()}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          {/* Warning Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
              <FaExclamationTriangle className="w-8 h-8 text-amber-500" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-gray-800 text-center mb-2">
            {t('embeddedBrowser.title', 'Open in Browser Required')}
          </h2>

          {/* Description */}
          <p className="text-gray-600 text-center mb-4 leading-relaxed">
            {t('embeddedBrowser.description', 'For security reasons, Google Sign-In is not available in this browser. Please open this page in Safari or Chrome.')}
          </p>
          
          {/* Detected Browser Badge */}
          {detectedBrowser && (
            <div className="flex justify-center mb-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
                {t('embeddedBrowser.detectedIn', 'Detected:')} {detectedBrowser}
              </span>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-blue-50 rounded-xl p-4 mb-6">
            <h3 className="font-semibold text-blue-800 mb-2">
              {t('embeddedBrowser.howTo', 'How to continue:')}
            </h3>
            <ol className={`text-sm text-blue-700 space-y-2 ${isRTL ? 'pr-4' : 'pl-4'}`}>
              <li>{t('embeddedBrowser.step1', '1. Tap the menu icon (⋯) in the top right')}</li>
              <li>{t('embeddedBrowser.step2', '2. Select "Open in Safari" or "Open in Browser"')}</li>
              <li>{t('embeddedBrowser.step3', '3. Or copy the link below and paste in your browser')}</li>
            </ol>
          </div>

          {/* URL Display */}
          <div className="bg-gray-100 rounded-lg p-3 mb-4 flex items-center gap-2">
            <input
              type="text"
              value={currentUrl}
              readOnly
              className="flex-1 bg-transparent text-sm text-gray-600 truncate outline-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <button
              onClick={handleCopyLink}
              className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition-all ${
                copied 
                  ? 'bg-green-500 text-white' 
                  : 'bg-[#2F80ED] text-white hover:bg-[#2563EB]'
              }`}
            >
              {copied ? (
                <>
                  <FaCheck className="w-4 h-4" />
                  {t('embeddedBrowser.copied', 'Copied!')}
                </>
              ) : (
                <>
                  <FaCopy className="w-4 h-4" />
                  {t('embeddedBrowser.copyLink', 'Copy Link')}
                </>
              )}
            </button>

            <button
              onClick={handleOpenInBrowser}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all"
            >
              <FaExternalLinkAlt className="w-4 h-4" />
              {t('embeddedBrowser.tryOpenBrowser', 'Try Open in Browser')}
            </button>

            <button
              onClick={onClose}
              className="w-full py-2 text-gray-500 hover:text-gray-700 text-sm font-medium transition-all"
            >
              {t('embeddedBrowser.useEmail', 'Continue with Email instead')}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EmbeddedBrowserModal;
